const normalizeRecordId = (value) => {
    const numericId = Number(value);
    return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

export const hasFileViewerRecordId = (file) => Boolean(normalizeRecordId(file?.id));

export const getFileViewerS3Key = (file) => {
    const key = file?.key || file?.file_path || file?.path;
    return typeof key === 'string' && key.trim() ? key : '';
};

const getFileViewerName = (file, s3Key) => {
    const explicitName = file?.file_name || file?.name || file?.filename;
    if (typeof explicitName === 'string' && explicitName.trim()) {
        return explicitName.trim();
    }

    const pathParts = String(s3Key || '').split('/');
    return pathParts[pathParts.length - 1] || '';
};

export const buildFileViewerUrl = ({ file, fileRecordId, caseId }) => {
    const resolvedFileId = normalizeRecordId(fileRecordId)
        || normalizeRecordId(file?.file_id)
        || normalizeRecordId(file?.id);
    if (resolvedFileId) {
        return `/file/${resolvedFileId}`;
    }

    const s3Key = getFileViewerS3Key(file);
    const resolvedCaseId = normalizeRecordId(caseId);
    if (!s3Key || !resolvedCaseId) {
        return null;
    }

    const params = new URLSearchParams();
    params.set('case_id', String(resolvedCaseId));
    params.set('s3_key', s3Key);

    const fileName = getFileViewerName(file, s3Key);
    if (fileName) {
        params.set('file_name', fileName);
    }

    return `/file/s3?${params.toString()}`;
};

export const buildFileViewerDownloadUrl = (file) => {
    const s3Key = getFileViewerS3Key(file);
    if (!s3Key) {
        return null;
    }

    const params = new URLSearchParams();
    params.set('key', s3Key);
    params.set('cases', 'true');
    params.set('download', 'true');
    params.set('download_name', getFileViewerName(file, s3Key) || 'download');

    const resolvedCaseId = normalizeRecordId(file?.case_id);
    if (resolvedCaseId) {
        params.set('case_id', String(resolvedCaseId));
    }

    return `https://api.casedb.co/get-signed-url.php?${params.toString()}`;
};

export const fetchFileViewerSource = async ({
    fileId,
    caseId,
    s3Key,
    fileName,
    fetchImpl = fetch,
}) => {
    if (s3Key) {
        const resolvedCaseId = normalizeRecordId(caseId);
        if (!resolvedCaseId) {
            throw new Error('A case ID is required to view this file');
        }

        const params = new URLSearchParams();
        params.set('key', s3Key);
        params.set('cases', 'true');
        params.set('case_id', String(resolvedCaseId));
        params.set('inline', 'true');

        const response = await fetchImpl(`https://api.casedb.co/get-signed-url.php?${params.toString()}`, {
            credentials: 'include',
        });
        const data = await response.json();

        if (response.ok === false || !data?.success || !data?.url) {
            throw new Error(data?.message || data?.error || 'File not found');
        }

        const verifiedKey = data.file_path || s3Key;
        const verifiedFileId = normalizeRecordId(data.file_id);
        const verifiedCaseId = normalizeRecordId(data.case_id) || resolvedCaseId;

        return {
            id: verifiedFileId,
            database_backed: Boolean(verifiedFileId && data.database_backed !== false),
            file_name: data.filename || getFileViewerName({ file_name: fileName }, verifiedKey),
            file_path: verifiedKey,
            key: verifiedKey,
            case_id: verifiedCaseId,
            url: data.url,
            signed_url: data.url,
            office_preview_url: data.office_preview_url || null,
            page_count: data.page_count,
            file_size: data.file_size ?? data.size ?? null,
            content_type: data.content_type || null,
        };
    }

    const resolvedFileId = normalizeRecordId(fileId);
    if (!resolvedFileId) {
        throw new Error('File viewer information is missing');
    }

    const params = new URLSearchParams();
    params.set('id', String(resolvedFileId));
    const response = await fetchImpl(`https://api.casedb.co/file-viewer.php?${params.toString()}`, {
        credentials: 'include',
    });
    const data = await response.json();

    if (response.ok === false || !data?.success || !data?.file) {
        throw new Error(data?.message || 'File not found');
    }

    const sourceFile = data.file;
    const sourceKey = sourceFile.file_path || sourceFile.key || sourceFile.path || s3Key || '';
    const resolvedName = sourceFile.file_name || getFileViewerName({ file_name: fileName }, sourceKey);
    const sourceUrl = sourceFile.flattened_signed_url
        || sourceFile.signed_url
        || sourceFile.file_url
        || sourceFile.url;

    if (!sourceUrl) {
        throw new Error('File preview URL is unavailable');
    }

    return {
        ...sourceFile,
        id: sourceFile.id || null,
        file_name: resolvedName,
        file_path: sourceKey,
        key: sourceFile.key || sourceKey,
        url: sourceUrl,
    };
};
